import { watch } from 'vue'
import type { UseInfiniteQueryReturnType } from '@tanstack/vue-query'

/**
 * Keeps fetching the next page of an infinite query until none is left, so
 * callers that need the whole catalog (id lookups, pickers) get every item
 * without a scroll-driven table.
 *
 * Stops while the query is erroring: a page fetch that exhausted its retries
 * leaves `isError` true with `hasNextPage` still reflecting the last
 * *successful* page, and re-firing would loop on the same failing page.
 * Callers see that as "still draining" and can check `isError` to tell the
 * two apart.
 */
export function useDrainPages(
  query: Pick<
    UseInfiniteQueryReturnType<unknown, Error>,
    'hasNextPage' | 'isFetchingNextPage' | 'isError' | 'fetchNextPage'
  >,
): void {
  watch(
    () => ({
      hasNext: query.hasNextPage.value === true,
      fetching: query.isFetchingNextPage.value === true,
      erroring: query.isError.value === true,
    }),
    ({ hasNext, fetching, erroring }) => {
      if (hasNext && !fetching && !erroring) void query.fetchNextPage()
    },
    { immediate: true, flush: 'post' },
  )
}
